import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormProvider } from "react-hook-form";
import { App, Flex } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";

import { useCreateDataBlockMutation } from "@/redux/service";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseModal from "@/components_v2/Modal/BaseModal";
import DataBlockTypeStep from "./DataBlockTypeStep";
import DataBlockDetailsForm from "./DataBlockDetailsForm";
import CancelConfirmationModal from "@/components_v2/Modal/CancelConfirmationModal";
import { useDataBlockSubmission } from "@/domains/datablocks/hooks/useDataBlockSubmission";
import { useDataBlockValidation } from "@/domains/datablocks/hooks/useDataBlockValidation";
import { useDataBlockForm } from "@/domains/datablocks/hooks/useDataBlockForm";
import { DataBlockProvider } from "@/domains/datablocks/providers/DataBlockProvider";
import { handleErrors } from "@/shared/utils/forms";
import {
  DataTemplateResponse,
  DynamicValueResponse,
} from "@/redux/datablocks/types";
import { useEnabledEvidenceTemplates } from "@/domains/evidence/hooks/useEvidenceTemplates";
import { BaseUserResponse } from "@/redux/users/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import {
  BUTTON_SIZES,
  BUTTON_TARGETS,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import {
  CreateDataBlockModalFooterProps,
  CreateDataBlockProps,
} from "@/domains/datablocks/types";

import "../styles.css";

const ModalFooter = ({
  currentStep,
  handleBack,
  handleNext,
  isNextEnabled,
  isSubmitting,
}: CreateDataBlockModalFooterProps) => {
  const { t } = useTranslation();

  return (
    <Flex wrap justify="space-between" align="center" gap={"small"}>
      <div>
        {currentStep > 0 && (
          <BaseButton
            variant={BUTTON_VARIANTS.TERTIARY}
            size={BUTTON_SIZES.LARGE}
            onClick={handleBack}
          >
            {t("buttons.back")}
          </BaseButton>
        )}

        {currentStep === 0 && (
          <BaseButton
            variant={BUTTON_VARIANTS.TERTIARY}
            size={BUTTON_SIZES.LARGE}
            leadingIcon={<InfoCircleOutlined />}
            type={BUTTON_TYPES.LINK}
            to={"/evidence/documentation"}
            target={BUTTON_TARGETS.BLANK}
          >
            {t("datablocks.labels.learnMore")}
          </BaseButton>
        )}
      </div>

      <div className="create-datablock-footer-buttons-group">
        <BaseButton
          onClick={() => handleNext(true)}
          loading={isSubmitting}
          disabled={currentStep === 0 && !isNextEnabled}
        >
          {t(
            currentStep === 0
              ? "buttons.nextStep"
              : "datablocks.labels.saveDataBlock",
          )}
        </BaseButton>
      </div>
    </Flex>
  );
};

const GuidedFirstStep = ({
  templateTitle,
  message,
  createdBy,
  date,
  error,
}: {
  templateTitle: string;
  message?: string;
  error?: { message: string };
  createdBy?: BaseUserResponse;
  date?: string;
}) => {
  const { t } = useTranslation();

  return (
    <div className="guided-step-container">
      <div className="guided-step-container-info-container">
        <div className="guided-step-container-info">
          <p className="body-m-regular">{t("datablocks.labels.from")}</p>
          <p className="body-m-medium">{createdBy?.fullName}</p>
        </div>
        <div className="guided-step-container-info">
          <p className="body-m-regular">{t("datablocks.labels.expiresOn")}</p>
          <p className="body-m-medium">{date?.slice(0, 10)}</p>
        </div>
        <div className="guided-step-container-info">
          <p className="body-m-regular">
            {t("datablocks.labels.dataTemplateType")}
          </p>
          <p className="body-m-medium">{templateTitle}</p>
        </div>
      </div>

      <h6 className="body-m-medium">{t("datablocks.labels.description")}</h6>
      <p className="body-s-regular">{message}</p>
      {error && <div className="error-message">{error.message}</div>}
    </div>
  );
};

const CreateDataBlockContent = ({
  open,
  setOpen,
  onSubmit,
  filters,
  defaultValues,
}: CreateDataBlockProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const currentUser = useSelector(selectCurrentUser);
  const [createDataBlock] = useCreateDataBlockMutation();
  const { isSubmitting, handleSubmit } = useDataBlockSubmission(currentUser);
  const { validationSchema, updateSchemaForTemplate } =
    useDataBlockValidation();
  const { form, initializeFormFieldValues } =
    useDataBlockForm(validationSchema);
  const enabledTemplates = useEnabledEvidenceTemplates(filters?.dataTemplates);
  const [currentStep, setCurrentStep] = useState(0);
  const [confirmCloseModal, setConfirmCloseModal] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const selectedTemplateId = form.watch("dataTemplate");

  const setCurrentStepDependingOnDefaultValues = () => {
    if (defaultValues?.dataTemplate && !defaultValues.request) {
      setCurrentStep(1);
    }
  };

  useEffect(() => {
    if (open) {
      setCurrentStepDependingOnDefaultValues();
    }
  }, [open]);

  useEffect(() => {
    if (defaultValues) {
      setCurrentStepDependingOnDefaultValues();
      form.reset(
        {
          dataTemplate: defaultValues?.dataTemplate,
          request: defaultValues?.request?.id,
        },
        { keepDefaultValues: true },
      );
    }
  }, [defaultValues]);

  const activeTemplate = useMemo(() => {
    if (enabledTemplates && selectedTemplateId) {
      return enabledTemplates.find(
        (i: DataTemplateResponse) => i.id === selectedTemplateId,
      );
    }
  }, [enabledTemplates, selectedTemplateId]);

  const setDefaultValues = () => {
    const setValueToField = (
      fieldId: number,
      value: DynamicValueResponse | DateTime,
    ) => {
      const fieldValues = form.getValues("fieldValues") || [];

      const existingFieldIndex = fieldValues.findIndex(
        (field) => field.field === fieldId,
      );
      const existingField = fieldValues[existingFieldIndex];
      if (existingField) {
        // @ts-expect-error datetime is an option for the value
        existingField.value = value;
      }
      return fieldValues;
    };

    if (currentUser && activeTemplate) {
      const contributorsField = activeTemplate.templateFields.find(
        (field) => field.id === activeTemplate.fieldsMapping.contributors,
      );
      const languageField = activeTemplate.templateFields.find(
        (field) => field.id === activeTemplate.fieldsMapping.languages,
      );
      const publishDateField = activeTemplate.templateFields.find(
        (field) => field.id === activeTemplate.fieldsMapping.publishDate,
      );
      let newFieldValues;
      if (contributorsField) {
        newFieldValues = setValueToField(contributorsField.id, [
          currentUser.id,
        ]);
      }
      const languageId = currentUser.organization?.language || undefined;
      if (languageField && languageId) {
        newFieldValues = setValueToField(languageField.id, [languageId]);
      }
      if (publishDateField) {
        newFieldValues = setValueToField(publishDateField.id, DateTime.now());
      }
      if (newFieldValues) form.setValue("fieldValues", newFieldValues);
    }
  };

  useEffect(() => {
    if (
      currentStep === 1 &&
      activeTemplate &&
      enabledTemplates &&
      currentUser
    ) {
      updateSchemaForTemplate(activeTemplate);
      initializeFormFieldValues(activeTemplate.templateFields);
      setDefaultValues();
    }
  }, [currentStep, activeTemplate, currentUser, enabledTemplates]);

  const renderSteps = useMemo(() => {
    if (!enabledTemplates) return;
    if (currentStep === 0) {
      if (defaultValues?.dataTemplate && defaultValues.request) {
        const template = enabledTemplates.find(
          (template) => template.id === defaultValues.dataTemplate,
        );
        if (template) {
          return (
            <GuidedFirstStep
              templateTitle={template.title}
              message={defaultValues.request?.message}
              createdBy={defaultValues.request?.createdBy}
              date={defaultValues?.request?.expiresAt}
            />
          );
        }
      }

      return (
        <DataBlockTypeStep
          templates={enabledTemplates}
          activeTemplateId={activeTemplate?.id}
          error={
            form.formState.errors?.dataTemplate?.message
              ? { message: form.formState.errors?.dataTemplate.message }
              : undefined
          }
          handleTemplateChange={(selectedTemplate: number) =>
            form.setValue("dataTemplate", selectedTemplate)
          }
        />
      );
    }

    if (currentStep === 1 && activeTemplate) {
      return <DataBlockDetailsForm dataTemplate={activeTemplate} />;
    }
  }, [
    enabledTemplates,
    form.formState.errors,
    activeTemplate,
    currentStep,
    defaultValues,
  ]);

  const modalTitle = useMemo(() => {
    if (currentStep === 0) {
      return t("datablocks.labels.createDataBlock");
    }
    return activeTemplate
      ? `${t("datablocks.labels.createDataBlock")} - ${activeTemplate.title}`
      : "";
  }, [currentStep, activeTemplate, t]);

  const resetModalHeight = () => {
    if (containerRef.current) {
      const modalBody = containerRef.current.querySelector(".ant-modal-body");
      if (modalBody) {
        modalBody.scrollTop = 0;
      }
    }
  };

  const handleNext = async () => {
    const isValid = await form.trigger(
      currentStep === 0 ? ["dataTemplate"] : ["sharingRights", "fieldValues"],
    );
    if (!isValid || !currentUser || isSubmitting) return;

    if (currentStep === 0) {
      setCurrentStep((prev) => prev + 1);
      resetModalHeight();
      return;
    }
    const formData = form.getValues();

    const disabledFieldIds = activeTemplate
      ? new Set(
          activeTemplate.templateFields
            .filter((field) => field.disabled)
            .map((field) => field.id),
        )
      : new Set<number>();

    const filteredFieldValues = formData.fieldValues.filter(
      (fieldValue) => !disabledFieldIds.has(fieldValue.field),
    );

    await handleSubmit(
      { ...formData, fieldValues: filteredFieldValues },
      (data) => createDataBlock(data).unwrap(),
      (result) => {
        notification.success({
          message: t("datablocks.labels.dataBlockSaved"),
        });
        setOpen(false);
        form.reset();
        if (onSubmit) {
          onSubmit(result);
        }
        setCurrentStep(0);
      },
      (errors) => {
        handleErrors(errors, form.setError, notification);
      },
    );
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
      resetModalHeight();
      form.reset(
        {
          dataTemplate: selectedTemplateId,
          sharingRights: undefined,
          fieldValues: [],
        },
        { keepDefaultValues: true },
      );
    }
  };

  return (
    <>
      <BaseModal
        classNames={{
          wrapper: "create-datablocks-modal",
        }}
        width={1000}
        open={open}
        maskClosable={false}
        onCancel={() => setConfirmCloseModal(true)}
        title={modalTitle}
        subtitle={
          currentStep === 0 && !defaultValues?.dataTemplate
            ? t("datablocks.labels.createBlockSubtitle")
            : undefined
        }
        footer={
          <ModalFooter
            currentStep={currentStep}
            handleBack={handleBack}
            handleNext={handleNext}
            isNextEnabled={selectedTemplateId != null}
            isSubmitting={isSubmitting}
          />
        }
        containerRef={containerRef}
      >
        <FormProvider {...form}>
          <div className="steps-container">{renderSteps}</div>
        </FormProvider>
      </BaseModal>

      <CancelConfirmationModal
        title={t("discardChanges")}
        content={t("discardChangesContent")}
        confirmButtonText={t("buttons.discard")}
        open={confirmCloseModal}
        onCancel={() => setConfirmCloseModal(false)}
        onConfirm={() => {
          setConfirmCloseModal(false);
          setOpen(false);
          setCurrentStep(0);
          form.reset();
        }}
      />
    </>
  );
};

const CreateDataBlock = (props: CreateDataBlockProps) => {
  return (
    <DataBlockProvider>
      <CreateDataBlockContent {...props} />
    </DataBlockProvider>
  );
};

export default CreateDataBlock;
